import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { LangSwitcher } from 'features/LangSwitcher';
import React, { FC, memo, ReactNode, useCallback, useMemo, useRef, useEffect } from 'react';
import { HeaderAuthAction } from 'features/HeaderAuthAction';
import { AppLogo, getGlobalSettings } from 'app/appState';
import { Button } from 'shared/ui/Button';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLoggedUserData } from 'entities/User';
import { getActiveStep } from '../model/selectors/getActiveStep';
import { getMaxProgressStep } from '../model/selectors/getMaxProgressStep';
import { getSteps } from '../model/selectors/getSteps';
import { bookingHeaderActions } from '../model/slice/bookingHeaderSlice';
import { BookingSteps } from '../model/types';
import { useWatchStep } from '../utils/hooks/useWatchStep';
import { ReactComponent as RightIcon } from 'shared/assets/icons/newIcons/RightArrow.svg';
import { ReactComponent as LeftIcon } from 'shared/assets/icons/newIcons/LeftArrow.svg';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/newIcons/LocationBookingIcon.svg';
import { ReactComponent as BoxIcon } from 'shared/assets/icons/newIcons/BoxIcon.svg';
import { ReactComponent as BookingIcon } from 'shared/assets/icons/newIcons/BookingIcon.svg';
import { ReactComponent as AccountIcon } from 'shared/assets/icons/newIcons/AccountIcon.svg';
import { ReactComponent as PaymentIcon } from 'shared/assets/icons/newIcons/PaymentIcon.svg';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';

export const ModernBookingHeader: FC = memo(() => {
  const { t } = useAppTranslation('booking');

  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef<HTMLDivElement>(null);

  const activeStep = useAppSelector(getActiveStep);
  const maxProgressStep = useAppSelector(getMaxProgressStep);
  const steps = useAppSelector(getSteps);
  const loggedUserData = useAppSelector(getLoggedUserData);
  const globalSettings = useAppSelector(getGlobalSettings);

  const currentStepIndex = steps.findIndex((step) => step.label === activeStep);
  const maxProgressStepIndex = steps.findIndex((step) => step.label === maxProgressStep);
  const hasPreviousStep = currentStepIndex > 0;
  const hasNextStep = currentStepIndex < maxProgressStepIndex;

  useWatchStep();

  // Auto-scroll to active step when it changes
  useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }, [activeStep]);

  const actions = useMemo<ReactNode[]>(() => {
    return [
      <LangSwitcher key="1" mobileBackgroundClassName="bg-backgroundColor" />,
      <HeaderAuthAction key="2" userEmail={loggedUserData?.email} />,
    ];
  }, [loggedUserData?.email]);

  const changeActiveStep = useCallback(
    (stepName: string): void => {
      dispatch(bookingHeaderActions.changeActiveStep(stepName as BookingSteps));
    },
    [dispatch],
  );

  const goToPreviousStep = useCallback((): void => {
    dispatch(bookingHeaderActions.goToPreviousStep());
  }, [dispatch]);

  const continueBooking = useCallback((): void => {
    dispatch(bookingHeaderActions.goToMaxProgressStep());
  }, [dispatch]);

  const getStepIcon = (stepLabel: string, isActive: boolean): JSX.Element => {
    const iconClass = isActive ? 'text-primary' : 'text-grey';

    switch (stepLabel) {
      case 'Select a location':
        return <LocationIcon className={`w-[16px] h-[16px] ${iconClass}`} />;
      case 'Select a unit':
        return <BoxIcon className={`w-[18px] h-[18px] ${iconClass}`} />;
      case 'Contract information':
        return <BookingIcon className={`w-[20px] h-[20px] ${iconClass}`} />;
      case 'Your details':
        return <AccountIcon className={`w-[21px] h-[21px] ${iconClass}`} />;
      case 'Payment':
        return <PaymentIcon className={`w-[20px] h-[20px] ${iconClass}`} />;
      default:
        return <LocationIcon className={`w-[16px] h-[16px] ${iconClass}`} />;
    }
  };

  const getStepLabel = (stepLabel: string): string => {
    switch (stepLabel) {
      case 'Select a location':
        return t('Select location');
      case 'Select a unit':
        return t('Select box');
      case 'Contract information':
        return t('Booking');
      case 'Your details':
        return t('Client data');
      case 'Payment':
        return t('Payment');
      default:
        return stepLabel;
    }
  };

  const homeUrl = loggedUserData ? AppRoutes.HOME : globalSettings?.landingUrl || AppRoutes.HOME;

  return (
    <div className="sticky top-0 z-20 modern-booking-header">
      {/* Container: white on desktop, backgroundColor on mobile */}
      <div className="pt-[20px] pb-2 bg-backgroundColor desktop:bg-white desktop:rounded-lg desktop:shadow-sm desktop:p-4 desktop:px-[50px] desktop:h-[70px]">
        {/* Desktop: single row; Mobile: two rows */}
        <div className="hidden desktop:flex items-center justify-between">
          <Link to={homeUrl} className="flex items-center shrink-0">
            <AppLogo />
          </Link>

          <div className="flex items-center mx-[50px] flex-1 min-w-0">
            <Button
              theme="outlined"
              size="medium"
              className="px-3 border-black shrink-0 mr-2"
              icon={<LeftIcon className="text-grey w-[16px] h-[16px] text-primary" />}
              isDisabled={!hasPreviousStep}
              onClick={goToPreviousStep}
            />

            <div ref={scrollContainerRef} className="flex-1 overflow-x-auto no-scrollbar">
              <div className="flex items-center justify-center min-w-max py-1">
                {steps.map((step, index) => {
                  const isActive = step.label === activeStep;
                  const isCompleted = index < currentStepIndex;

                  return (
                    <div key={step.label} className="flex items-center shrink-0">
                      <div
                        ref={isActive ? activeStepRef : null}
                        className={`step-item flex items-center space-x-2 transition-all duration-200 ${
                          isActive ? 'text-primary' : isCompleted ? 'text-primary cursor-pointer' : 'text-grey cursor-not-allowed'
                        } ${!isActive && !isCompleted ? 'hover:text-primary' : ''}`}
                        onClick={() => {
                          if (isActive || isCompleted) {
                            changeActiveStep(step.label);
                          }
                        }}
                      >
                        <div className="step-icon">{getStepIcon(step.label, isActive || isCompleted)}</div>
                        <span
                          className={`text-xl font-medium font-tektur transition-colors whitespace-nowrap ${
                            isActive ? 'text-primary' : isCompleted ? 'text-primary' : 'text-grey'
                          }`}
                        >
                          {getStepLabel(step.label)}
                        </span>
                      </div>

                      {index < steps.length - 1 && <div className="step-separator mx-4 text-grey">&#8212;</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              theme="outlined"
              size="medium"
              className="px-3 border-black shrink-0 ml-2"
              icon={<RightIcon className="text-grey w-[16px] h-[16px] text-primary" />}
              isDisabled={!hasNextStep}
              onClick={continueBooking}
            />
          </div>

          <div className="flex items-center space-x-3 shrink-0">{actions}</div>
        </div>

        {/* Mobile two-rows layout */}
        <div className="desktop:hidden">
          {/* Row 1: centered logo with actions on the right */}
          <div className="relative flex items-center justify-center px-4 py-3">
            <div className="flex items-center">
              <Link to={homeUrl} className="flex items-center shrink-0">
                <AppLogo />
              </Link>
            </div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center space-x-1">{actions}</div>
          </div>

          {/* Row 2: active step centered with prev/next controls */}
          <div className="flex items-center py-2 px-4">
            <Button
              theme="outlined"
              size="small"
              className="w-[40px] h-[40px] p-0 border-black shrink-0 mr-2 rounded-lg flex items-center justify-center"
              icon={<LeftIcon className="text-grey w-[16px] h-[16px] text-primary" />}
              isDisabled={!hasPreviousStep}
              onClick={goToPreviousStep}
            />

            <div ref={scrollContainerRef} className="flex-1">
              <div className="w-full flex items-center justify-center py-1">
                {(() => {
                  const active = steps.find((s) => s.label === activeStep);
                  if (!active) return null;
                  return (
                    <div className="flex items-center shrink-0">
                      <div ref={activeStepRef} className="step-item flex items-center space-x-2 transition-all duration-200 text-primary">
                        <div className="step-icon">{getStepIcon(active.label, true)}</div>
                        <span className="text-22 font-medium font-tektur transition-colors whitespace-nowrap text-primary">
                          {getStepLabel(active.label)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <Button
              theme="outlined"
              size="small"
              className="w-[40px] h-[40px] p-0 border-black shrink-0 ml-2 rounded-lg flex items-center justify-center"
              icon={<RightIcon className="text-grey w-[16px] h-[16px] text-primary" />}
              isDisabled={!hasNextStep}
              onClick={continueBooking}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
